import { useEffect, useState } from "react";
import { ListSearchSort } from "@/components/ListSearchSort";
import { sortByName, matchesQuery } from "@/lib/listSort";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Package, Plus, X, CheckCircle2, AlertCircle, Repeat, Trash2, Pencil, Store, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  active: "#CCFF00",
  completed: "#007AFF",
  cancelled: "#A3A3A3",
  expired: "#FF4500",
};

export default function CoachPackages() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canMarkPaid = (user?.permissions || []).some((p) => ["manual_mark_package_paid", "admin", "super_admin"].includes(p));
  const isAdmin = user?.permissions?.includes("admin") || user?.permissions?.includes("super_admin") || user?.role === "admin";
  const [packages, setPackages] = useState([]);
  const [storeBundles, setStoreBundles] = useState([]);   // Iter96 — open bundles from /offerings (is_package=true), managed inline here
  const [students, setStudents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  // Iter95 — Publishing mode. "assign" is the legacy private path (pick one
  // student, backend `POST /packages`). "store" is the open path — publishes
  // an offering with is_package=true so any student can browse & buy from
  // the Store tab on /offerings. Coaches often want BOTH: bespoke pre-paid
  // packs for regulars AND an off-the-shelf pack anyone can grab.
  const [mode, setMode] = useState("assign");
  const [form, setForm] = useState({
    student_id: "",
    sport: "pickleball",
    type: "block",
    sessions_total: 4,
    price: 200,
    expires_at: "",
    auto_renew: false,
    paid: false,
    covers_assessment: false,
    notes: "",
    refund_policy: "",
    allow_promo_codes: true,  // iter97 — store mode only; default ON
  });
  const [filterStudent, setFilterStudent] = useState("");
  const [pkgQuery, setPkgQuery] = useState("");
  const [pkgSort, setPkgSort] = useState("default");
  const [filterStatus, setFilterStatus] = useState("active");

  const load = async () => {
    try {
      const showCancelled = filterStatus === "cancelled" || filterStatus === "all";
      const [pks, sts, bundles] = await Promise.all([
        api.get(`/packages${showCancelled ? "?include_cancelled=true" : ""}`),
        api.get("/students"),
        // Iter96 — Open bundles the coach owns (backend scopes to coach_id when role=coach).
        api.get("/offerings?packages_only=true"),
      ]);
      setPackages(pks.data || []);
      setStudents(sortByName(sts.data || [], (s) => s.name, "az"));
      // Admins see everyone's bundles from /offerings; scope client-side to
      // the coach's own so the list stays personal (matches assigned packages).
      const raw = Array.isArray(bundles.data) ? bundles.data : [];
      const mine = isAdmin ? raw : raw.filter((b) => b.coach_id === user?.id);
      setStoreBundles(mine);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load packages");
    }
  };

  useEffect(() => { load(); }, [filterStatus]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.sessions_total || form.sessions_total < 1) { toast.error("Sessions must be at least 1"); return; }
    if (mode === "store") {
      // Publish an open bundle to the Store — any student can buy via Stripe.
      // Backend endpoint: POST /api/offerings with is_package=true. This is
      // the SAME schema used by Offerings → New → Package, so the store card
      // lands next to any other package published there.
      if (!form.price || Number(form.price) < 1) { toast.error("Store bundles need a price of at least £1"); return; }
      // Derive expiry_months from optional expires_at, else default 6 months.
      let expiryMonths = 6;
      if (form.expires_at) {
        const diffMs = new Date(form.expires_at).getTime() - Date.now();
        expiryMonths = Math.max(1, Math.min(36, Math.ceil(diffMs / (30 * 24 * 3600 * 1000))));
      }
      const sportLabel = form.sport === "squash" ? "Squash" : "Pickleball";
      const title = `${form.sessions_total}-Session ${sportLabel} Pack`;
      try {
        await api.post("/offerings", {
          sport: form.sport,
          session_type: "group",
          skill_level: "intermediate",
          skill_focus: [],
          venue: "",
          title,
          start: null,
          duration_min: 0,
          max_spaces: 0,
          price_per_player: Number(form.price),
          notes: form.refund_policy || form.notes || "",
          is_assessment: false,
          shared_with_team: false,
          save_as_template: false,
          region_ids: [],
          is_package: true,
          session_count: Number(form.sessions_total),
          expiry_months: expiryMonths,
          accept_credits: false,
          allow_promo_codes: !!form.allow_promo_codes,
        });
        toast.success("Package published to the Store — any student can buy it");
        setShowForm(false);
        setForm({ ...form, student_id: "", sessions_total: 4, price: 200, notes: "", refund_policy: "" });
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Failed to publish");
      }
      return;
    }
    if (!form.student_id) { toast.error("Pick a student"); return; }
    try {
      await api.post("/packages", {
        student_id: form.student_id,
        sport: form.sport,
        type: form.type,
        sessions_total: Number(form.sessions_total),
        price: Number(form.price),
        expires_at: form.expires_at || null,
        auto_renew: form.type === "monthly" ? Boolean(form.auto_renew) : false,
        paid: Boolean(form.paid),
        covers_assessment: Boolean(form.covers_assessment),
        notes: form.notes,
        refund_policy: form.refund_policy,
      });
      toast.success("Package created");
      setShowForm(false);
      setForm({ ...form, student_id: "", sessions_total: 4, price: 200, notes: "", refund_policy: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create");
    }
  };

  const togglePaid = async (pkg) => {
    try {
      await api.patch(`/packages/${pkg.id}`, { paid: !pkg.paid });
      toast.success(pkg.paid ? "Marked unpaid" : "Marked paid");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cancel = async (pkg) => {
    if (!window.confirm(`Cancel "${pkg.sessions_total} sessions" package for ${pkg.student_name}? Used sessions are kept; remaining ones are lost.`)) return;
    try {
      await api.delete(`/packages/${pkg.id}`);
      toast.success("Cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Iter96 — Manage open store bundles from this page. Edit hops to the
  // reusable OfferingNew form in edit mode (title/price/session_count/expiry).
  // Archive is a hard delete via the shared offering endpoint — packages have
  // no ongoing bookings so this is safe (any minted credit pack is already
  // detached on the buyer's user record and keeps its expiry).
  const editBundle = (b) => {
    nav(`/offerings/new?edit_id=${b.id}`);
  };
  const archiveBundle = async (b) => {
    if (!window.confirm(`Archive "${b.title}" from the store? Students who already bought it keep their credits — this only stops new purchases.`)) return;
    try {
      await api.delete(`/offerings/${b.id}`);
      toast.success("Bundle archived");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to archive"); }
  };

  const filteredRaw = packages.filter((p) => {
    if (filterStudent && p.student_id !== filterStudent) return false;
    if (filterStatus !== "all" && p.status !== filterStatus) return false;
    return true;
  });
  const filtered = sortByName(filteredRaw.filter((p) => matchesQuery(pkgQuery, p.student_name, p.name, p.title)), (p) => p.student_name, pkgSort);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4 fade-up">
        <div>
          <div className="label-mono mb-2">PERFORMANCE PACKAGES</div>
          <h1 className="h-display text-5xl tracking-tighter">Block coaching.</h1>
          <div className="text-sm text-[#A3A3A3] mt-1 max-w-2xl">
            Sell Performance sessions in <span className="text-white">blocks</span> (one-off pack of N sessions) or as a <span className="text-white">monthly</span> programme.
            Once paid, the student redeems them straight from the Offerings page — no Stripe checkout needed.
          </div>
        </div>
        <button
          data-testid="new-package-btn"
          onClick={() => setShowForm(true)}
          className="btn-volt px-5 py-3 text-xs inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> New package
        </button>
      </div>

      {/* Iter96 — Open store bundles. Managed inline so the coach doesn't
          have to hop to /offerings. Coach can edit price/session_count/expiry
          and archive any bundle right here. Displayed above the assigned-
          package filters so open catalogue always sits at the top. */}
      {storeBundles.length > 0 && (
        <div className="fade-up d1" data-testid="store-bundles-section">
          <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="label-mono text-[#CCFF00] inline-flex items-center gap-1.5">
                <Store className="w-3.5 h-3.5" /> OPEN STORE BUNDLES
              </div>
              <div className="text-sm text-[#A3A3A3] mt-1">
                On sale to any student via <Link to="/offerings" className="underline text-white hover:text-[#CCFF00]">Offerings → Packages</Link>. Edit or archive without leaving this page.
              </div>
            </div>
            <div className="text-xs text-[#A3A3A3]">{storeBundles.length} live</div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {storeBundles.map((b) => (
              <div key={b.id} className="card-surface p-5" data-testid={`bundle-${b.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0">
                    <div className="label-mono text-[#CCFF00] inline-flex items-center gap-1"><Store className="w-3 h-3" /> STORE BUNDLE</div>
                    <div className="h-display text-xl mt-1 truncate">{b.title}</div>
                    <div className="text-[10px] text-[#A3A3A3] mt-1 uppercase tracking-wider">{b.sport}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="label-mono">£</div>
                    <div className="h-display text-2xl">{b.price_per_player}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="border border-[#262626] p-2">
                    <div className="label-mono text-[#A3A3A3]">SESSIONS</div>
                    <div className="text-white text-lg font-bold mt-0.5">{b.session_count}</div>
                  </div>
                  <div className="border border-[#262626] p-2">
                    <div className="label-mono text-[#A3A3A3]">EXPIRES</div>
                    <div className="text-white text-lg font-bold mt-0.5">{b.expiry_months} mo</div>
                  </div>
                </div>
                {b.notes && <div className="text-[11px] text-[#A3A3A3] italic mb-3 line-clamp-2">&ldquo;{b.notes}&rdquo;</div>}
                <div className="flex items-center gap-2 pt-2 border-t border-[#262626]">
                  <button
                    onClick={() => editBundle(b)}
                    className="text-xs text-[#CCFF00] hover:opacity-70 inline-flex items-center gap-1"
                    data-testid={`bundle-${b.id}-edit`}
                  >
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <Link
                    to="/offerings"
                    onClick={() => { try { localStorage.setItem("rl_offerings_tab", "packages"); } catch (_e) { /* silent */ } }}
                    className="text-xs text-[#A3A3A3] hover:text-white inline-flex items-center gap-1"
                    data-testid={`bundle-${b.id}-view`}
                  >
                    <ExternalLink className="w-3 h-3" /> View in store
                  </Link>
                  <button
                    onClick={() => archiveBundle(b)}
                    className="text-xs text-[#FF4500] hover:opacity-70 inline-flex items-center gap-1 ml-auto"
                    data-testid={`bundle-${b.id}-archive`}
                  >
                    <Trash2 className="w-3 h-3" /> Archive
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card-surface p-4 flex flex-wrap gap-3 items-end fade-up d1">
        <div>
          <div className="label-mono mb-1">Student</div>
          <select
            data-testid="filter-student"
            value={filterStudent}
            onChange={(e) => setFilterStudent(e.target.value)}
            className="bg-[#141414] border border-[#262626] px-3 py-2 text-sm"
          >
            <option value="">All students</option>
            {students.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="label-mono mb-1">Status</div>
          <select
            data-testid="filter-status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-[#141414] border border-[#262626] px-3 py-2 text-sm"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="expired">Expired</option>
          </select>
        </div>
        <div className="text-xs text-[#A3A3A3] ml-auto">
          {filtered.length} of {packages.length} packages
        </div>
      </div>

      {/* List */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 fade-up d2">
        {filtered.length === 0 && (
          <div className="card-surface p-8 col-span-full text-center" data-testid="packages-empty">
            <Package className="w-10 h-10 text-[#525252] mx-auto mb-3" />
            <div className="text-sm text-[#A3A3A3]">No packages yet. Click <span className="text-white">New package</span> to create one.</div>
          </div>
        )}
        <ListSearchSort query={pkgQuery} onQuery={setPkgQuery} sort={pkgSort} onSort={setPkgSort} modes={["default", "az", "za"]} placeholder="Search by player or pack…" testid="packages" />
        {filtered.map((p) => {
          const sc = STATUS_COLOR[p.status] || "#A3A3A3";
          const usedPct = p.sessions_total > 0 ? Math.min(100, Math.round((p.sessions_used / p.sessions_total) * 100)) : 0;
          return (
            <div key={p.id} className="card-surface p-5" data-testid={`pkg-${p.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="label-mono mb-1" style={{ color: sc }}>{p.status?.toUpperCase()}</div>
                  <Link to={`/players/${p.student_id}`} className="h-display text-2xl tracking-tight hover:text-[#CCFF00]" data-testid={`pkg-${p.id}-student`}>
                    {p.student_name}
                  </Link>
                </div>
                <div className="text-right">
                  <div className="label-mono">£</div>
                  <div className="h-display text-2xl">{p.price}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="label-mono px-2 py-0.5 border border-[#262626]">{p.type === "monthly" ? "MONTHLY" : "BLOCK"}</span>
                <span className="label-mono px-2 py-0.5 border border-[#262626]">{p.sport?.toUpperCase()}</span>
                {p.auto_renew && (
                  <span className="label-mono px-2 py-0.5 border border-[#262626] text-[#CCFF00] inline-flex items-center gap-1">
                    <Repeat className="w-3 h-3" /> AUTO-RENEW
                  </span>
                )}
                {p.covers_assessment && (
                  <span className="label-mono px-2 py-0.5 border border-[#FF4500] text-[#FF4500]" data-testid={`pkg-${p.id}-assessment-badge`}>
                    COVERS ASSESSMENT
                  </span>
                )}
                {p.paid ? (
                  <span className="label-mono text-[#CCFF00] inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> PAID
                  </span>
                ) : (
                  <span className="label-mono text-[#FF4500] inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> UNPAID
                  </span>
                )}
              </div>
              <div className="mb-3">
                <div className="flex items-baseline gap-2">
                  <div className="h-display text-3xl" style={{ color: sc }}>{p.sessions_remaining}</div>
                  <div className="label-mono text-[#A3A3A3]">/ {p.sessions_total} REMAINING</div>
                </div>
                <div className="mt-2 h-1 bg-[#262626] overflow-hidden">
                  <div className="h-full" style={{ width: `${usedPct}%`, background: sc }} />
                </div>
                <div className="text-[10px] text-[#A3A3A3] mt-1">{p.sessions_used} used · {usedPct}%</div>
              </div>
              {p.expires_at && (
                <div className="text-xs text-[#A3A3A3] mb-3">Expires: {String(p.expires_at).slice(0,10)}</div>
              )}
              {p.notes && (
                <div className="text-xs text-[#A3A3A3] italic mb-3">&ldquo;{p.notes}&rdquo;</div>
              )}
              <div className="flex gap-2 pt-2 border-t border-[#262626]">
                {p.status === "active" && (
                  <>
                    {canMarkPaid && (
                      <button
                        onClick={() => togglePaid(p)}
                        className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] transition-colors"
                        data-testid={`pkg-${p.id}-toggle-paid`}
                      >
                        {p.paid ? "Mark unpaid" : "Mark paid"}
                      </button>
                    )}
                    <button
                      onClick={() => cancel(p)}
                      className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#FF4500] hover:opacity-70 inline-flex items-center gap-1 ml-auto"
                      data-testid={`pkg-${p.id}-cancel`}
                    >
                      <Trash2 className="w-3 h-3" /> Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="new-package-modal">
          <div className="bg-[#0A0A0A] border border-[#262626] w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="label-mono mb-1">{mode === "store" ? "PUBLISH TO STORE" : "NEW PERFORMANCE PACKAGE"}</div>
                <h2 className="h-display text-2xl tracking-tight">
                  {mode === "store" ? "Publish an open pack." : "Assign a block / monthly pack."}
                </h2>
              </div>
              <button onClick={() => setShowForm(false)} className="p-1 text-[#A3A3A3] hover:text-white" data-testid="new-package-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Iter95 — Mode toggle. Coaches often want both: bespoke packs
                assigned privately to regulars, AND an off-the-shelf pack any
                student can browse & buy. Switching to "store" hides the
                student picker + mark-paid + assessment cover (irrelevant for
                open bundles paid via Stripe). */}
            <div className="grid grid-cols-2 gap-2 mb-5" data-testid="pkg-mode-picker">
              <button
                type="button"
                onClick={() => setMode("assign")}
                data-testid="pkg-mode-assign"
                className="px-3 py-2.5 text-xs uppercase tracking-wider border transition-colors"
                style={mode === "assign"
                  ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00", fontWeight: 700 }
                  : { borderColor: "#262626", color: "#A3A3A3" }}
              >
                Assign to a student
              </button>
              <button
                type="button"
                onClick={() => setMode("store")}
                data-testid="pkg-mode-store"
                className="px-3 py-2.5 text-xs uppercase tracking-wider border transition-colors"
                style={mode === "store"
                  ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00", fontWeight: 700 }
                  : { borderColor: "#262626", color: "#A3A3A3" }}
              >
                Publish to store (open)
              </button>
            </div>
            {mode === "store" && (
              <div className="text-[11px] text-[#A3A3A3] border border-[#262626] bg-[#141414] p-3 mb-4" data-testid="store-mode-hint">
                Any student can browse & buy this pack from <span className="text-white">Offerings → Packages</span>. Payment runs through Stripe — no student assignment needed. Once bought, credits appear in their account automatically.
              </div>
            )}
            <form onSubmit={submit} className="space-y-4">
              {mode === "assign" && (
                <div>
                  <div className="label-mono mb-1">Student</div>
                  <select
                    data-testid="pkg-student"
                    required
                    value={form.student_id}
                    onChange={(e) => setForm({ ...form, student_id: e.target.value })}
                    className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm"
                  >
                    <option value="">— select a student —</option>
                    {students.map((s) => (
                      <option key={s.id} value={s.id}>{s.name} · {s.email}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="label-mono mb-1">Sport</div>
                  <select
                    data-testid="pkg-sport"
                    value={form.sport}
                    onChange={(e) => setForm({ ...form, sport: e.target.value })}
                    className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm"
                  >
                    <option value="pickleball">Pickleball</option>
                    <option value="squash">Squash</option>
                  </select>
                </div>
                <div>
                  <div className="label-mono mb-1">Type</div>
                  <select
                    data-testid="pkg-type"
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    disabled={mode === "store"}
                    className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm disabled:opacity-50"
                  >
                    <option value="block">Block — N sessions, one-off</option>
                    {mode !== "store" && <option value="monthly">Monthly — recurring</option>}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="label-mono mb-1">Sessions in pack</div>
                  <input
                    data-testid="pkg-sessions"
                    type="number" min={1} max={50} required
                    value={form.sessions_total}
                    onChange={(e) => setForm({ ...form, sessions_total: e.target.value })}
                    className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <div className="label-mono mb-1">Price {form.type === "monthly" ? "(per month)" : "(total)"} (£)</div>
                  <input
                    data-testid="pkg-price"
                    type="number" min={0} step={0.5} required
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="label-mono mb-1">Expires (optional)</div>
                  <input
                    data-testid="pkg-expires"
                    type="date"
                    value={form.expires_at}
                    onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                    className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm"
                  />
                </div>
                <div className="flex items-center gap-4 pt-6">
                  {mode === "assign" && form.type === "monthly" && (
                    <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="pkg-auto-renew-label">
                      <input
                        data-testid="pkg-auto-renew"
                        type="checkbox"
                        checked={form.auto_renew}
                        onChange={(e) => setForm({ ...form, auto_renew: e.target.checked })}
                      />
                      Auto-renew
                    </label>
                  )}
                  {mode === "assign" && (canMarkPaid ? (
                    <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="pkg-paid-label">
                      <input
                        data-testid="pkg-paid"
                        type="checkbox"
                        checked={form.paid}
                        onChange={(e) => setForm({ ...form, paid: e.target.checked })}
                      />
                      Mark paid
                    </label>
                  ) : (
                    <div className="text-[10px] text-[#A3A3A3] italic max-w-xs" data-testid="pkg-paid-locked">
                      Students pay via Stripe from their dashboard. Ask an admin for the &ldquo;manual mark paid&rdquo; permission if you also collect payments offline.
                    </div>
                  ))}
                  {mode === "store" && (
                    <div className="text-[10px] text-[#A3A3A3] italic max-w-xs" data-testid="pkg-store-payment-hint">
                      Students pay by card at checkout. You don&apos;t need to mark anything paid.
                    </div>
                  )}
                </div>
              </div>
              {mode === "assign" && (
                <label className="flex items-start gap-2 text-xs cursor-pointer p-3 border border-[#262626] hover:border-[#FF4500] transition-colors" data-testid="pkg-covers-assessment-label">
                  <input
                    data-testid="pkg-covers-assessment"
                    type="checkbox"
                    checked={form.covers_assessment}
                    onChange={(e) => setForm({ ...form, covers_assessment: e.target.checked })}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="font-semibold text-[#FF4500]">Covers assessment fee</div>
                    <div className="text-[10px] text-[#A3A3A3] mt-0.5">
                      When ticked, the student can redeem 1 session from this pack to pay for their initial assessment.
                      Leave unticked if assessments must always be paid separately.
                    </div>
                  </div>
                </label>
              )}
              {mode === "assign" && (
                <div>
                  <div className="label-mono mb-1">Notes (internal)</div>
                  <textarea
                    data-testid="pkg-notes"
                    rows={2}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm"
                    placeholder="e.g. Paid by bank transfer, valid until Aug"
                  />
                </div>
              )}
              <div>
                <div className="label-mono mb-1">{mode === "store" ? "Refund policy / description (shown to student)" : "Refund policy (shown to student)"}</div>
                <textarea
                  data-testid="pkg-refund-policy"
                  rows={2}
                  maxLength={1000}
                  value={form.refund_policy}
                  onChange={(e) => setForm({ ...form, refund_policy: e.target.value })}
                  className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm"
                  placeholder="e.g. Full refund within 7 days if no sessions attended. No refund after first session."
                />
                <div className="text-[10px] text-[#A3A3A3] mt-1">
                  {mode === "store"
                    ? "Shown on the store card so buyers know what they're getting. Leave blank if you don't have a policy."
                    : "Displayed in the student's Pending offers card. Leave blank if you don't have a policy."}
                </div>
              </div>
              {/* Iter97/98 — Allow-promo lock (store mode only). Copy explains
                  (1) no stacking and (2) two-sided targeting (coach tick + admin
                  allowlist) so nobody expects a random code to work here. */}
              {mode === "store" && (
                <label className="flex items-start gap-2 text-xs cursor-pointer p-3 border border-[#262626] hover:border-[#CCFF00] transition-colors" data-testid="pkg-allow-promo-label">
                  <input
                    type="checkbox"
                    data-testid="pkg-allow-promo"
                    checked={!!form.allow_promo_codes}
                    onChange={(e) => setForm({ ...form, allow_promo_codes: e.target.checked })}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="font-semibold text-white">Allow discount codes on this package</div>
                    <div className="text-[10px] text-[#A3A3A3] mt-0.5 leading-relaxed">
                      {form.allow_promo_codes ? (
                        <>
                          Only <span className="text-white">one code per checkout</span> — codes never stack.
                          Codes apply here only if the admin has left the code&apos;s allowlist blank
                          (universal) or added this pack to it via <span className="text-white">Admin → Promo codes</span>.
                        </>
                      ) : (
                        <>This pack is ring-fenced — <span className="text-white">no code</span> will apply, even active ones.</>
                      )}
                    </div>
                  </div>
                </label>
              )}
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  data-testid="pkg-create"
                  className="btn-volt px-5 py-3 text-xs inline-flex items-center gap-2 flex-1 justify-center"
                >
                  <Plus className="w-4 h-4" /> {mode === "store" ? "Publish to store" : "Create package"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-5 py-3 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#FF4500] hover:text-[#FF4500]"
                  data-testid="pkg-cancel-create"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
